import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Plus, Edit, Trash2, X, Save, LayoutGrid } from 'lucide-react';
import { getToken } from '../../services/tokenService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${getToken()}`,
  'Content-Type': 'application/json'
});

const BLOCK_TYPES = [
  { value: 'text', label: 'Texte', icon: '📝' },
  { value: 'image', label: 'Image', icon: '🖼️' },
  { value: 'text_image', label: 'Texte + Image', icon: '🖌️' },
  { value: 'stats', label: 'Statistiques', icon: '📊' },
  { value: 'card', label: 'Carte', icon: '🎴' },
  { value: 'hero', label: 'Hero', icon: '🎆' }
];

const BlocksManager = () => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBlock, setEditingBlock] = useState(null);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    loadBlocks();
  }, [filterType]);

  const loadBlocks = async () => {
    setLoading(true);
    try {
      const typeParam = filterType !== 'all' ? `?block_type=${filterType}` : '';
      const response = await fetch(`${API_URL}/api/cms/blocks${typeParam}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setBlocks(data.blocks || []);
    } catch {
    }
    setLoading(false);
  };

  const handleCreateBlock = () => {
    setEditingBlock({
      name: 'Nouveau bloc',
      type: 'text',
      content: {},
      style: {}
    });
  };

  const handleSaveBlock = async () => {
    try {
      const url = editingBlock.id
        ? `${API_URL}/api/cms/blocks/${editingBlock.id}`
        : `${API_URL}/api/cms/blocks`;
      
      const method = editingBlock.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(editingBlock)
      });

      if (response.ok) {
        alert('✅ Bloc enregistré !');
        setEditingBlock(null);
        loadBlocks();
      } else {
        alert('❌ Erreur lors de la sauvegarde');
      }
    } catch {
      alert('❌ Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm('Supprimer ce bloc ?')) return;

    try {
      const response = await fetch(`${API_URL}/api/cms/blocks/${blockId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        loadBlocks();
      }
    } catch {
    }
  };

  const renderBlockEditor = () => {
    if (!editingBlock) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Card className="max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>
                {editingBlock.id ? 'Éditer le bloc' : 'Nouveau bloc'}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setEditingBlock(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Nom du bloc</label>
                <Input
                  value={editingBlock.name}
                  onChange={(e) => setEditingBlock({ ...editingBlock, name: e.target.value })}
                  placeholder="Ex: Hero Homepage"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Type de bloc</label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={editingBlock.type}
                  onChange={(e) => setEditingBlock({ ...editingBlock, type: e.target.value })}
                >
                  {BLOCK_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Content Editor based on type */}
            <div>
              <label className="block text-sm font-medium mb-2">Contenu (JSON)</label>
              <Textarea
                value={JSON.stringify(editingBlock.content, null, 2)}
                onChange={(e) => {
                  try {
                    const content = JSON.parse(e.target.value);
                    setEditingBlock({ ...editingBlock, content });
                  } catch {
                    // Invalid JSON, keep typing
                  }
                }}
                rows={10}
                className="font-mono text-xs"
              />
              <p className="text-xs text-gray-500 mt-1">
                Format JSON. Exemple pour hero: {'{"title": "...", "subtitle": "...", "cta_text": "...", "cta_url": "..."}'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Style (JSON)</label>
              <Textarea
                value={JSON.stringify(editingBlock.style, null, 2)}
                onChange={(e) => {
                  try {
                    const style = JSON.parse(e.target.value);
                    setEditingBlock({ ...editingBlock, style });
                  } catch {
                    // Invalid JSON
                  }
                }}
                rows={6}
                className="font-mono text-xs"
              />
              <p className="text-xs text-gray-500 mt-1">
                CSS-like properties: {'{"background_color": "#1e40af", "text_color": "#ffffff"}'}
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingBlock(null)}>
                Annuler
              </Button>
              <Button onClick={handleSaveBlock}>
                <Save className="h-4 w-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Gestionnaire de Blocs</CardTitle>
              <p className="text-sm text-gray-500 mt-1">{blocks.length} bloc(s)</p>
            </div>
            <Button onClick={handleCreateBlock}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau bloc
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {/* Filters */}
          <div className="mb-6 flex gap-2 flex-wrap">
            <Button
              variant={filterType === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType('all')}
            >
              Tous
            </Button>
            {BLOCK_TYPES.map(type => (
              <Button
                key={type.value}
                variant={filterType === type.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType(type.value)}
              >
                {type.icon} {type.label}
              </Button>
            ))}
          </div>

          {/* Blocks List */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Chargement...</p>
            </div>
          ) : blocks.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
              <LayoutGrid className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 mb-2">Aucun bloc trouvé</p>
              <Button onClick={handleCreateBlock} variant="outline" size="sm">
                Créer votre premier bloc
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {blocks.map(block => {
                const typeInfo = BLOCK_TYPES.find(t => t.value === block.type);
                return (
                  <Card key={block.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="text-2xl mb-1">{typeInfo?.icon || '📄'}</div>
                          <h3 className="font-semibold text-lg">{block.name}</h3>
                          <p className="text-xs text-gray-500">{typeInfo?.label || block.type}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingBlock(block)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteBlock(block.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                        <pre className="overflow-x-auto">
                          {JSON.stringify(block.content, null, 1).substring(0, 100)}...
                        </pre>
                      </div>
                      <div className="mt-2 text-xs text-gray-400">
                        Créé le {new Date(block.created_at).toLocaleDateString()}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {renderBlockEditor()}
    </>
  );
};

export default BlocksManager;